import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";
import {
  CreateCompanyRequestDTO,
  CreateCompanyResponseDTO,
  ListCompaniesResponseDTO,
  CompanyDTO,
} from "../models/CompanyDTOs";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// Create company
export const createCompany = async (
  data: CreateCompanyRequestDTO
): Promise<CreateCompanyResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const url = `${API_BASE_URL}/api/companies`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

// List companies
export const listCompanies = async (
  page: number = 1,
  limit: number = 50,
  search?: string
): Promise<ListCompaniesResponseDTO> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const url = `${API_BASE_URL}/api/companies/list`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ page, limit, search: search?.trim() || "" }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

// Update company
export const updateCompany = async (
  id: number,
  data: Partial<CompanyDTO>
): Promise<{ success: boolean; message: string; data: { company: CompanyDTO } }> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const url = `${API_BASE_URL}/api/companies/update`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ id, ...data }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

// Delete company
export const deleteCompany = async (
  id: number
): Promise<{ success: boolean; message: string }> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const url = `${API_BASE_URL}/api/companies/delete`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ id }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

// Get company details with users
export const getCompany = async (
  companyId: number
): Promise<{
  success: boolean;
  message: string;
  data: {
    company: CompanyDTO;
    users: Array<{
      id: number;
      email: string;
      userName?: string;
      is_active: boolean;
      is_master_admin: boolean;
      role_name: string | null;
      is_company_admin: boolean;
    }>;
    /** The onboarding draft this company grew out of, or null if it never was one. */
    onboarding: {
      public_id: string;
      name: string;
      status: string;
      live_at: string | null;
    } | null;
  };
}> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const url = `${API_BASE_URL}/api/companies/get`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ id: companyId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

// Get users for a company
export const getCompanyUsers = async (
  companyId: number
): Promise<{
  success: boolean;
  message: string;
  data: {
    users: Array<{
      id: number;
      email: string;
      userName?: string;
      is_active: boolean;
      is_master_admin: boolean;
      role_name: string | null;
      is_company_admin: boolean;
    }>;
    company: {
      id: number;
      company_name: string;
      app_url: string;
    };
  };
}> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const url = `${API_BASE_URL}/api/companies/users`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ company_id: companyId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    return await response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

